/**
 * Follows one AI generation's live progress over Pusher, as a small
 * external store (so the state can outlive the component that shows it:
 * the editor's AI keeps running while its dialog is closed).
 */

import { useSyncExternalStore } from 'react';
import { pusher } from '../../../hooks/pusher';
import { AI_PROGRESS_EVENT, aiProgressChannel, createAiProgressId, INITIAL_AI_PROGRESS, parseAiProgressEvent, reduceAiProgress, type AiProgressState } from './aiProgressState';

export interface AiRunState {
  running: boolean;
  /** Date.now() at the start of the current run (null when idle). */
  startedAt: number | null;
  progress: AiProgressState;
}

const IDLE: AiRunState = { running: false, startedAt: null, progress: INITIAL_AI_PROGRESS };
/** Longest wait for the channel subscription before the request goes out anyway. */
const SUBSCRIBE_WAIT_MS = 1500;

export class AiProgressTracker {
  private state: AiRunState = IDLE;
  private readonly listeners = new Set<() => void>();
  private channelName: string | null = null;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): AiRunState => this.state;

  private set(next: AiRunState): void {
    this.state = next;
    for (const listener of this.listeners) listener();
  }

  /**
   * Starts a run: subscribes to a fresh progress channel and resolves with
   * its id once subscribed (or after a short wait: progress is optional).
   */
  async begin(): Promise<string> {
    this.end();
    const id = createAiProgressId();
    const name = aiProgressChannel(id);
    this.channelName = name;
    this.set({ running: true, startedAt: Date.now(), progress: INITIAL_AI_PROGRESS });
    try {
      const channel = pusher.subscribe(name);
      channel.bind(AI_PROGRESS_EVENT, (data: unknown) => {
        const event = parseAiProgressEvent(data);
        if (event && this.channelName === name) this.set({ ...this.state, progress: reduceAiProgress(this.state.progress, event) });
      });
      // Only wait when a socket is (being) opened: without Pusher (local dev) there is nothing to wait for.
      const state = pusher.connection.state;
      if (!channel.subscribed && (state === 'connected' || state === 'connecting' || state === 'initialized')) {
        await new Promise<void>((resolve) => {
          const timer = setTimeout(resolve, SUBSCRIBE_WAIT_MS);
          channel.bind('pusher:subscription_succeeded', () => {
            clearTimeout(timer);
            resolve();
          });
        });
      }
    } catch {
      /* no live progress: the UI falls back to estimates */
    }
    return id;
  }

  /** Stops following (the run finished, failed or was abandoned). */
  end(): void {
    if (this.channelName) {
      try {
        pusher.unsubscribe(this.channelName);
      } catch {
        /* already gone */
      }
    }
    this.channelName = null;
    if (this.state !== IDLE) this.set(IDLE);
  }
}

export function useAiRunState(tracker: AiProgressTracker): AiRunState {
  return useSyncExternalStore(tracker.subscribe, tracker.getSnapshot);
}
